import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { MapLoadCause } from '@voll-intelligence/domain';

import { useGenUiTelemetry } from '../../shared/ui/telemetry.js';
import type { PlaceDetails } from '@voll-intelligence/genui-contract';
import {
  loadCause,
  loadMaps,
  watchFirstPaint,
  type MapInstance,
  type MapsApi,
  type MarkerInstance,
} from '../shared/google_maps_loader.js';

/** Uma repetição automática antes do estado de erro: uma falha momentânea do
 *  script (dev 17/09) deixava o mapa morto até recarregar a página. */
const AUTO_RETRIES = 1;

export interface NumberedMapPlace {
  readonly place: PlaceDetails;
  readonly position: number;
}

/** O mapa nasce no 1º lugar da lista (São Paulo sem nenhum). */
function initialMapOptions(first: NumberedMapPlace | undefined): Record<string, unknown> {
  return {
    center: {
      lat: first?.place.location.latitude ?? -23.5505,
      lng: first?.place.location.longitude ?? -46.6333,
    },
    zoom: 13,
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    mapTypeControl: false,
  };
}

/** O mapa que não veio: a mensagem e a saída, anunciadas a quem usa leitor de tela. */
function MapFallback({
  errorLabel,
  retryLabel,
  onRetry,
}: {
  readonly errorLabel: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="absolute inset-0 grid place-items-center bg-muted px-6 text-center text-sm text-muted-foreground"
    >
      <div className="space-y-3">
        <p>{errorLabel}</p>
        <button
          type="button"
          className="rounded-full bg-card/95 px-3 py-2 text-sm font-semibold shadow"
          onClick={onRetry}
        >
          {retryLabel}
        </button>
      </div>
    </div>
  );
}

export function PlacesGoogleMap({
  apiKey,
  places,
  anchorPlace,
  selectedId,
  onSelect,
  loadingLabel,
  errorLabel,
  retryLabel,
  ariaLabel,
  expanded = false,
}: {
  readonly apiKey: string;
  readonly places: readonly NumberedMapPlace[];
  readonly anchorPlace?: PlaceDetails;
  readonly selectedId?: string;
  readonly onSelect: (placeId: string) => void;
  readonly loadingLabel: string;
  readonly errorLabel: string;
  readonly retryLabel: string;
  readonly ariaLabel: string;
  readonly expanded?: boolean;
}) {
  const telemetry = useGenUiTelemetry();
  const telemetryRef = useRef(telemetry);
  const frameRef = useRef<HTMLDivElement>(null);
  const mapsRef = useRef<MapsApi | undefined>(undefined);
  const markersRef = useRef(new Map<string, MarkerInstance>());
  const onSelectRef = useRef(onSelect);
  const selectedIdRef = useRef(selectedId);
  // Só o centro da carga lê os lugares: trocá-los move os pinos, não refaz o mapa.
  const placesRef = useRef(places);
  // Renovados a cada commit: só os efeitos e a carga assíncrona os leem.
  useLayoutEffect(() => {
    telemetryRef.current = telemetry;
    selectedIdRef.current = selectedId;
    placesRef.current = places;
  });
  /** 1 = a carga inicial; 2 = a repetição automática; 3+ = "Tentar de novo".
   *  Preso à CHAVE: uma chave nova recomeça do 1 e ganha a sua repetição. */
  const [attempts, setAttempts] = useState({ apiKey, attempt: 1 });
  const attempt = attempts.apiKey === apiKey ? attempts.attempt : 1;
  /** A carga corrente (chave + tentativa): o mapa e a falha são DELA, e uma carga
   *  nova nasce sem os dois no mesmo render. */
  const load = `${apiKey}\u001f${String(attempt)}`;
  const [failedLoad, setFailedLoad] = useState<string | null>(null);
  const failed = failedLoad === load;
  /** A instância viva, em estado e não em ref: os pinos dependem DELA. Numa
   *  reconstrução com o mapa anterior de pé, um booleano `ready` podia ir de true a
   *  true (o `false` do efeito e o `true` do microtask no mesmo lote) e os pinos não
   *  voltariam; a identidade nova sempre os põe no mapa novo. */
  const [built, setBuilt] = useState<{ load: string; map: MapInstance } | null>(null);
  const map = built?.load === load ? built.map : undefined;
  const nextAttempt = useCallback(() => {
    setAttempts((current) => ({
      apiKey,
      attempt: (current.apiKey === apiKey ? current.attempt : 1) + 1,
    }));
  }, [apiKey]);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    const markers = markersRef.current;
    let disposed = false;
    let stopWatch: (() => void) | undefined;
    const fail = (cause: MapLoadCause) => {
      telemetryRef.current.track('map_load_failed', { cause, attempt });
      if (attempt <= AUTO_RETRIES) nextAttempt();
      else setFailedLoad(load);
    };
    void loadMaps(apiKey)
      .then((maps) => {
        const frame = frameRef.current;
        if (disposed || !frame) return;
        mapsRef.current = maps;
        const instance = new maps.Map(frame, initialMapOptions(placesRef.current[0]));
        // O SDK aceitar o `new Map` não quer dizer que pintou (#4034).
        stopWatch = watchFirstPaint(frame, instance, () => {
          fail('no_tiles');
        });
        setBuilt({ load, map: instance });
      })
      .catch((error: unknown) => {
        if (!disposed) fail(loadCause(error));
      });
    return () => {
      disposed = true;
      stopWatch?.();
      for (const marker of markers.values()) marker.setMap(null);
      markers.clear();
      mapsRef.current = undefined;
    };
    // A key change must build a new authenticated map instance; a new attempt
    // reloads the SDK (or rebuilds the map on a fresh frame). Place updates are
    // handled below.
  }, [apiKey, attempt, load, nextAttempt]);

  useEffect(() => {
    const maps = mapsRef.current;
    if (!maps || !map) return;
    for (const marker of markersRef.current.values()) marker.setMap(null);
    markersRef.current.clear();

    const bounds = new maps.LatLngBounds();
    for (const { place, position: listPosition } of places) {
      const position = { lat: place.location.latitude, lng: place.location.longitude };
      bounds.extend(position);
      const marker = new maps.Marker({
        map,
        position,
        label: String(listPosition),
        title: place.name,
        zIndex: place.placeId === selectedIdRef.current ? 100 : listPosition,
      });
      marker.addListener('click', () => {
        onSelectRef.current(place.placeId);
      });
      markersRef.current.set(place.placeId, marker);
    }
    if (anchorPlace) {
      const position = {
        lat: anchorPlace.location.latitude,
        lng: anchorPlace.location.longitude,
      };
      bounds.extend(position);
      const marker = new maps.Marker({
        map,
        position,
        label: '◆',
        title: anchorPlace.name,
        zIndex: 90,
      });
      markersRef.current.set(`anchor:${anchorPlace.placeId}`, marker);
    }
    if (places.length + (anchorPlace ? 1 : 0) > 1) map.fitBounds(bounds, 48);
  }, [anchorPlace, places, map]);

  useEffect(() => {
    for (const [placeId, marker] of markersRef.current) {
      marker.setZIndex(placeId === selectedId ? 100 : placeId.startsWith('anchor:') ? 90 : 1);
    }
  }, [selectedId]);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-muted">
      {/* Um contêiner por tentativa: o SDK não desmonta um mapa, e a reconstrução
          (repetição ou "Tentar de novo") nasce num nó limpo. */}
      <div
        key={attempt}
        ref={frameRef}
        className={expanded ? 'h-[70dvh] min-h-80 w-full' : 'h-60 w-full'}
        aria-label={ariaLabel}
      />
      {failed ? (
        <MapFallback errorLabel={errorLabel} retryLabel={retryLabel} onRetry={nextAttempt} />
      ) : map ? null : (
        <span className="sr-only">{loadingLabel}</span>
      )}
    </div>
  );
}
